import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { makeTheme, type ThemeMode } from './styles/theme';
import { ThemeModeProvider, getInitialMode } from './styles/ThemeMode';
import { isAuthenticated } from './utils/auth';
import AdminLayout from './components/layout/AdminLayout';
import LoginPage from './pages/login/LoginPage';
import SetPasswordPage from './pages/login/SetPasswordPage';
import StaffList from './pages/staff/StaffList';
import NewsList from './pages/news/NewsList';
import NewsEditor from './pages/news/NewsEditor';
import AppealsList from './pages/appeals/AppealsList';
import DirectoryList from './pages/directory/DirectoryList';
import BrandLogos from './pages/directory/BrandLogos';
import ShutoffsList from './pages/shutoffs/ShutoffsList';
import CityNoticesPage from './pages/notices/CityNotices';
import UsersList from './pages/users/UsersList';
import Dashboard from './pages/dashboard/Dashboard';
import DistrictsList from './pages/geo/DistrictsList';
import DistrictsMap from './pages/geo/DistrictsMap';
import CityObjects from './pages/geo/CityObjects';
import StreetsList from './pages/geo/StreetsList';
import PollsList from './pages/polls/PollsList';
import PushCenter from './pages/push/PushCenter';
import TransportPage from './pages/transport/TransportPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import AppointmentsList from './pages/appointments/AppointmentsList';
import EcologyPage from './pages/ecology/EcologyPage';
import VillagePage from './pages/village/VillagePage';
import EventsList from './pages/events/EventsList';
import ClassifiedsList from './pages/classifieds/ClassifiedsList';
import JobsList from './pages/jobs/JobsList';
import ChatModeration from './pages/chat/ChatModeration';
import CurrencyPage from './pages/currency/CurrencyPage';
import HolidaysPage from './pages/holidays/HolidaysPage';
import DeliveryPromosPage from './pages/delivery/DeliveryPromosPage';
import CinemaPage from './pages/cinema/CinemaPage';
import AkimatDirectory from './pages/akimat/AkimatDirectory';
import AkimatDocuments from './pages/akimat/AkimatDocuments';
import HeatingPage from './pages/heating/HeatingPage';
import DataSyncPage from './pages/datasync/DataSyncPage';
import AssistantLogs from './pages/assistant/AssistantLogs';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  return (
    <ConfigProvider theme={makeTheme(mode)} locale={ruRU}>
      <ThemeModeProvider mode={mode} setMode={setMode}>
      <AntApp>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Ссылки из писем: приглашение и восстановление пароля */}
            <Route path="/accept-invite" element={<SetPasswordPage />} />
            <Route path="/reset-password" element={<SetPasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="news" element={<NewsList />} />
              <Route path="news/create" element={<NewsEditor />} />
              <Route path="news/:id/edit" element={<NewsEditor />} />
              <Route path="appeals" element={<AppealsList />} />
              <Route path="directory" element={<DirectoryList />} />
              <Route path="brand-logos" element={<BrandLogos />} />
              <Route path="shutoffs" element={<ShutoffsList />} />
              <Route path="city-notices" element={<CityNoticesPage />} />
              <Route path="users" element={<UsersList />} />
              <Route path="staff" element={<StaffList />} />
              <Route path="districts" element={<DistrictsList />} />
              <Route path="districts-map" element={<DistrictsMap />} />
              <Route path="streets" element={<StreetsList />} />
              <Route path="polls" element={<PollsList />} />
              <Route path="push" element={<PushCenter />} />
              <Route path="transport" element={<TransportPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="appointments" element={<AppointmentsList />} />
              <Route path="akimat-directory" element={<AkimatDirectory />} />
              <Route path="akimat-documents" element={<AkimatDocuments />} />
              <Route path="ecology" element={<EcologyPage />} />
              <Route path="village" element={<VillagePage />} />
              <Route path="events" element={<EventsList />} />
              <Route path="classifieds" element={<ClassifiedsList />} />
              <Route path="jobs" element={<JobsList />} />
              <Route path="chat" element={<ChatModeration />} />
              <Route path="currency" element={<CurrencyPage />} />
              <Route path="holidays" element={<HolidaysPage />} />
              <Route path="delivery-promos" element={<DeliveryPromosPage />} />
              <Route path="cinema" element={<CinemaPage />} />
              <Route path="heating" element={<HeatingPage />} />
              <Route path="city-objects" element={<CityObjects />} />
              <Route path="data-sync" element={<DataSyncPage />} />
              <Route path="assistant" element={<AssistantLogs />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AntApp>
      </ThemeModeProvider>
    </ConfigProvider>
  );
};

export default App;
